import { create } from "zustand";
import type { AuthMembership, AuthUser } from "@marketing/shared";
import { getActiveWorkspace, setActiveWorkspace } from "@/lib/api";

type AuthState = {
  user: AuthUser | null;
  /** Workspace the dashboard is currently showing. */
  workspaceId: string | null;
  setUser: (user: AuthUser | null) => void;
  switchWorkspace: (id: string) => void;
  activeMembership: () => AuthMembership | null;
  reset: () => void;
};

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  workspaceId: getActiveWorkspace(),

  setUser: (user) => {
    // Keep the stored workspace only if the user still belongs to it — a
    // membership can be revoked between sessions, and a stale id would make
    // every request 404.
    const current = get().workspaceId;
    const valid = user?.memberships.some((m) => m.workspaceId === current) ? current : null;
    const next = valid ?? user?.memberships[0]?.workspaceId ?? null;
    setActiveWorkspace(next);
    set({ user, workspaceId: next });
  },

  switchWorkspace: (id) => {
    const user = get().user;
    if (!user?.memberships.some((m) => m.workspaceId === id)) return;
    setActiveWorkspace(id);
    set({ workspaceId: id });
  },

  activeMembership: () => {
    const { user, workspaceId } = get();
    return user?.memberships.find((m) => m.workspaceId === workspaceId) ?? null;
  },

  reset: () => {
    setActiveWorkspace(null);
    set({ user: null, workspaceId: null });
  },
}));
